import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { FixedPriceMutationDto, FixedPriceOverviewDto, FixedPriceType } from '../../../config/registrationConfig'
import ModifyRegistrationRow, {
    ModifyRegistrationColumnProps,
    ModifyRegistrationColumns,
    ModifyRegistrationRowProps,
    useRegistrationRowStyles,
} from '../Registration/ModifyRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { multiplyRate } from '../../../helpers/numberHelper'
import { FixedPriceListProps } from './FixedPriceList'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../Combobox/i18n/VatTypeCombobox'
import { Textarea, TextareaProps } from '@fluentui/react-components'
import FixedPriceTypeCombobox, { FixedPriceTypeComboboxProps } from '../../Combobox/FixedPriceTypeCombobox'
import useFixedPriceTypes from '../../../hooks/useFixedPriceTypes'
import { MoneyDto, VatType } from '../../../config/i18nConfig'
import useVatTypes from '../../../hooks/useVatTypes'
import FormikField from '../../Formik/FormikField'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import FormikComboboxField from '../../Formik/FormikComboboxField'

type Props = ModifyRegistrationRowProps<FixedPriceOverviewDto, FixedPriceMutationDto, FixedPriceListProps>
type ColumnProps = ModifyRegistrationColumnProps<FixedPriceListProps>

function Columns(props: ColumnProps): ReactElement {
    const registrationStyles = useRegistrationRowStyles()
    const { values, setFieldValue } = useFormikContext<FixedPriceMutationDto>()
    const { fixedPriceTypes, getFixedPriceType } = useFixedPriceTypes()
    const { getVatType } = useVatTypes()
    const fixedPriceType = useMemo<FixedPriceType>(() => getFixedPriceType(values.type), [values.type])
    const vatType = useMemo<VatType>(() => getVatType(values.vatType), [values.vatType])
    const total = useMemo<MoneyDto>(
        () =>
            multiplyRate((vatType?.percentage ?? 0) / 100 + 1, {
                amount: values.rate,
                currency: values.currency,
            }),
        [values.rate, vatType],
    )

    //When there is no long_description set use the name as name and the description as description.
    //When there is a long_description set use the description as name and the long_description as description.
    const useName = useMemo<boolean>(() => fixedPriceTypes.filter(type => !!type.longDescription).length === 0, [fixedPriceTypes])

    switch (props.column.key) {
        case 'type':
            return (
                fixedPriceTypes.length > 0 && (
                    <FormikComboboxField<FixedPriceMutationDto, FixedPriceTypeComboboxProps>
                        fieldName={'type'}
                        component={FixedPriceTypeCombobox}
                        size={'small'}
                        componentProps={{
                            selectedOptions: [values.type],
                            onOptionSelect: (_, data) => {
                                const fixedPriceTypes = getFixedPriceType(data.selectedOptions?.[0])
                                setFieldValue('type', data.selectedOptions?.[0])
                                    .then(() => setFieldValue('vatType', fixedPriceTypes?.vatType))
                                    .then(() => setFieldValue('description', fixedPriceTypes?.longDescription ?? fixedPriceTypes?.description))
                                    .then(() => setFieldValue('rate', fixedPriceTypes?.rate ?? '0.00'))
                            },
                            getEntityLabel: entity => (useName ? entity.name : entity.description),
                            className: useName ? registrationStyles.type : registrationStyles.typeWide,
                            input: {
                                className: useName ? registrationStyles.typeInput : registrationStyles.typeWideInput,
                            },
                        }}
                    />
                )
            )
        case 'description':
            return (
                <FormikField<TextareaProps, FixedPriceMutationDto>
                    fieldName={'description'}
                    component={Textarea}
                    componentProps={{
                        size: 'small',
                        className: registrationStyles.description,
                        resize: 'vertical',
                    }}
                />
            )
        case 'rate':
            return (
                (!fixedPriceType || fixedPriceType?.prefillType) && (
                    <FormikField<MoneyInputProps, FixedPriceMutationDto>
                        fieldName={'rate'}
                        component={MoneyInput}
                        componentProps={{
                            size: 'small',
                            currency: values.currency,
                            allowNegative: true,
                            onCommit: (_, value: string) => setFieldValue('rate', value),
                            className: registrationStyles.rate,
                            input: { className: registrationStyles.rateInput },
                        }}
                    />
                )
            )
        case 'vat':
            return (
                (!fixedPriceType || fixedPriceType?.prefillType) && (
                    <FormikComboboxField<FixedPriceMutationDto, VatTypeComboboxProps>
                        fieldName={'vatType'}
                        component={VatTypeCombobox}
                        componentProps={{
                            size: 'small',
                            selectedOptions: [values.vatType],
                            onOptionSelect: (_, data) => setFieldValue('vatType', data.selectedOptions?.[0]),
                            className: registrationStyles.vat,
                            input: { className: registrationStyles.vatInput },
                        }}
                    />
                )
            )
        case 'total':
            return (!fixedPriceType || fixedPriceType?.prefillType) && <MoneyText value={total} align="right" />
    }
    return <ModifyRegistrationColumns<FixedPriceMutationDto, FixedPriceListProps> {...props} />
}

export default function ModifyFixedPriceRow(props: Props): ReactElement {
    return <ModifyRegistrationRow<FixedPriceOverviewDto, FixedPriceMutationDto, FixedPriceListProps> {...props} columns={Columns} />
}
